import { useLocalStore, useObserver } from 'mobx-react';
import { ScoreDetailService } from '../../common/services';
import { runInAction, toJS } from 'mobx';
import WebSocketUtil from '../../common/utils/socketUtil';
import ChampWebSocketUtil from '../../common/utils/champSocketUtil';
import MatchCheerCommentModel from './model';
import { LocalStorageUtil } from '../../common/utils/localStorageUtil';

const IS_VIEW_ALL_CHEER = 'isViewAllCheer';

const MatchCheerStore = () => {
  const store = useLocalStore(() => ({
    comments: [],
    powerChat: {
      comments: [],
      totalCount: 0,
      currentSlide: 1,
    },
    loading: false,
    init: false,
    socket: null,
    topics: [],
    isSub: false,
    gameId: null,
    sportsType: null,
    sportsId: null,
    page: 1,
    total_cnt: 0,
    ref: null,
    report: {
      is_real: false,
      is_fake: false,
      is_crime: false,
    },
    isAll: LocalStorageUtil.get(IS_VIEW_ALL_CHEER) === 'true',

    champSocket: null,
    champIsSub: false,
    champGameId: null,
    champComments: [],

    removeAllTopics(topics) {
      if (store.socket && topics?.length > 0) {
        topics.forEach((data) => {
          store.socket.unsub(data);
        });
      }
    },
    addTopics(topics) {
      store.topics = topics;
      if (store.socket && topics?.length > 0) {
        topics.forEach((data) => {
          store.socket.sub(data);
        });
      }
    },
    connectSocket(topic) {
      store.socket = new WebSocketUtil(topic);
      store.socket.ws.onmessage = (event) => {
        if (event) {
          let data = event.data;
          try {
            if (data === 'OK') {
              store.isSub = true;
            } else {
              let messageData = JSON.parse(data);
              if (messageData) {
                const { MESSAGE } = messageData;
                const data = JSON.parse(MESSAGE);
                if (store.gameId !== data.game_id) data.differentGame = true;
                const commentData = new MatchCheerCommentModel(data);

                if (data.message_type === 'POWER_CHAT') {
                  store.powerChat.totalCount = parseInt(store.powerChat.totalCount) + 1;
                  store.powerChat.comments.splice(store.powerChat.currentSlide, 0, commentData);
                }

                store.comments = [commentData, ...store.comments];

                if (store.ref.scrollHeight - store.ref.scrollTop <= store.ref.clientHeight + 150) {
                  setTimeout(() => (store.ref.scrollTop = store.ref.scrollHeight), 100);
                }
              }
            }
          } catch (error) {
            console.log('=== socket on message error ===', error);
          }
        }
      };
    },
    closeSocket(callback) {
      if (store.socket) {
        const currentSocket = store.socket;
        console.log('Closing existing WebSocket connection');

        if (currentSocket.ws) {
          console.log('Socket readyState before close:', currentSocket.ws.readyState);

          if (currentSocket.ws.readyState === WebSocket.CONNECTING) {
            console.warn('WebSocket is still connecting, delaying close...');
            currentSocket.ws.onopen = () => {
              console.log('Connection opened after delay, now closing...');
              currentSocket.ws.close();
            };
            return;
          }

          currentSocket.ws.onmessage = null;
          currentSocket.ws.onerror = null;

          currentSocket.ws.onclose = () => {
            console.log('WebSocket closed');
            runInAction(() => {
              store.isSub = false;
              if (store.socket === currentSocket) {
                store.socket = null;
              }
              if (callback) callback();
            });
          };

          currentSocket.ws.close();

          // Fallback cleanup if onclose is not called in time
          setTimeout(() => {
            if (store.socket === currentSocket) {
              console.warn('WebSocket onclose not fired, forcing cleanup');
              runInAction(() => {
                store.socket = null;
                store.isSub = false;
              });
              if (callback) callback();
            }
          }, 500);
        } else {
          store.socket = null;
          store.isSub = false;
          if (callback) callback();
        }
      } else {
        if (callback) callback();
      }
    },

    champConnectSocket(topic) {
      store.champSocket = new ChampWebSocketUtil(topic);
      store.champSocket.ws.onmessage = (event) => {
        if (event) {
          let data = event.data;
          try {
            if (data === 'OK') {
              store.champisSub = true;
            } else {
              let messageData = JSON.parse(data);
              if (messageData) {
                const { TOPIC, MESSAGE } = messageData;
                const data = JSON.parse(MESSAGE);

                const commentData = new MatchCheerCommentModel(data);
                if (commentData.is_admin) return;

                commentData.isChamp = true;

                if (store.ref.scrollHeight - store.ref.scrollTop <= store.ref.clientHeight + 150) {
                  setTimeout(() => (store.ref.scrollTop = store.ref.scrollHeight), 100);
                }

                store.comments = [commentData, ...store.comments];
              }
            }
          } catch (error) {
            console.log('=== socket on message error ===', error);
          }
        }
      };
    },
    closeChampSocket() {
      if (!!store.champSocket) {
        store.champSocket.close(() => {
          runInAction(() => {
            store.champisSub = false;
            store.champSocket = null;
          });
        });
      }
    },

    setRef(ref) {
      store.ref = ref;
    },

    // Do not disconnect and re-connect the socket when the user changes the view mode
    // instead, just update the topics and let the socket handle the rest
    changeViewMode(value) {
      store.isAll = value;
      store.removeAllTopics(store.topics || []);

      if (value) {
        store.addTopics([
          'sports.soccer.cheers',
          'sports.baseball.cheers',
          'sports.basketball.cheers',
          'sports.volleyball.cheers',
          'sports.football.cheers',
          'sports.hockey.cheers',
          'sports.star.cheers',
          'sports.lol.cheers',
        ]);
        LocalStorageUtil.set(IS_VIEW_ALL_CHEER, true);
      } else {
        store.addTopics([`sports.${store.sportsType}.cheers.${store.gameId}`]);
        LocalStorageUtil.remove(IS_VIEW_ALL_CHEER);
      }
    },
    async getCheerList(gameId, sportsType, sportsId, page = 1) {
      store.loading = true;
      store.gameId = gameId;
      store.sportsType = sportsType;
      store.sportsId = sportsId;

      // const { id } = await ScoreDetailService.getChampGameId(gameId);
      // if (!!id) {
      //   store.champGameId = id;
      //   const champResult = await ScoreDetailService.getChampCheerList(
      //     id,
      //     sportsId
      //   );
      //   if (!!champResult) {
      //     store.champComments = champResult.list.map((item) => {
      //       item.isChamp = true;
      //       return new MatchCheerCommentModel(item);
      //     });
      //   }

      //   if (!store.champIsSub) {
      //     store.champConnectSocket([`sports.${sportsType}.cheers.${id}`]);
      //   }
      // }

      if (!store.isSub) {
        if (store.isAll) {
          store.connectSocket([
            'sports.soccer.cheers',
            'sports.baseball.cheers',
            'sports.basketball.cheers',
            'sports.volleyball.cheers',
            'sports.football.cheers',
            'sports.hockey.cheers',
            'sports.star.cheers',
            'sports.lol.cheers',
          ]);
        } else {
          store.connectSocket([`sports.${sportsType}.cheers${gameId ? `.${gameId}` : ''}`]);
        }
      }

      try {
        const result = await ScoreDetailService.getCheerList(gameId, sportsId, page);

        if (!!result) {
          store.total_cnt = result.total_cnt;
        }

        if (result.list.length > 0) {
          const list = result.list.map((item) => new MatchCheerCommentModel(item));
          const filteredChampList = store.champComments.filter((item) => {
            return item.createAtMoment >= list[list.length - 1].createAtMoment && !item.is_admin;
          });

          store.comments = list.concat(filteredChampList).sort((a, b) => {
            return b.createAtMoment - a.createAtMoment;
          });
        } else {
          store.comments = [...store.champComments];
        }

        if (result.power_chat.list.length > 0) {
          store.powerChat.comments = result.power_chat.list.map((item) => new MatchCheerCommentModel(item));
          store.powerChat.totalCount = result.power_chat.total_cnt;
        }
      } catch (e) {}
      store.loading = false;
    },

    async getCheerMore(height) {
      const { page, total_cnt, gameId, sportsId } = store;
      const maxPage = Math.round(total_cnt / 200);
      let nextPage = page + 1;

      if (nextPage > maxPage) {
        const filteredChampList = store.champComments.filter((item) => {
          return item.createAtMoment < store.comments[store.comments.length - 1].createAtMoment && !item.is_admin;
        });
        store.comments = [...store.comments, ...filteredChampList];
        setTimeout(() => {
          store.ref.scrollTop = store.ref.scrollHeight - height;
        }, 1);
        return;
      } else {
        store.page = nextPage;
      }

      const result = await ScoreDetailService.getCheerList(gameId, sportsId, nextPage);

      if (result.list.length > 0) {
        const addList = result.list.map((item) => new MatchCheerCommentModel(item));
        const filteredChampList = store.champComments.filter((item) => {
          return (
            item.createAtMoment < store.comments[store.comments.length - 1].createAtMoment &&
            item.createAtMoment >= addList[addList.length - 1].createAtMoment &&
            !item.is_admin
          );
        });

        store.comments = store.comments.concat(filteredChampList, addList).sort((a, b) => {
          return b.createAtMoment - a.createAtMoment;
        });

        setTimeout(() => (store.ref.scrollTop = store.ref.scrollHeight - height), 1);
      }
    },
    async postCheerComment(param) {
      const data = {
        ...param,
        sportsId: store.sportsId,
      };
      const result = await ScoreDetailService.postCheer(store.gameId, data);

      return result;
    },
    async postCheerCommentReport(param) {
      const data = {
        ...param,
        sports_id: store.sportsId,
      };

      const result = await ScoreDetailService.postCommentReport(data);

      if (result.status === 200) {
        store.report = result.data;
      }

      return result;
    },
    async getCheerCommentReport(targetId, gameId) {
      const result = await ScoreDetailService.getCommentReport(targetId, gameId, store.sportsId);
      if (result) {
        store.report = result;
      }
    },
    async deletePowerChat(chat, index) {
      const result = await ScoreDetailService.deleteCheer(chat);
      if (result.status === 200) {
        store.powerChat.comments.splice(index, 1);
        store.powerChat.totalCount = store.powerChat.totalCount - 1;
      } else {
        alert(result.data.message);
      }
    },
    changeCurrentSlide(index) {
      store.powerChat.currentSlide = index + 1;
    },
  }));

  return {
    getCheerList: store.getCheerList,
    getCheerMore: store.getCheerMore,
    postCheerComment: store.postCheerComment,
    postCheerCommentReport: store.postCheerCommentReport,
    getCheerCommentReport: store.getCheerCommentReport,
    changeCurrentSlide: store.changeCurrentSlide,
    closeSocket: store.closeSocket,
    closeChampSocket: store.closeChampSocket,
    setRef: store.setRef,
    changeViewMode: store.changeViewMode,
    deletePowerChat: store.deletePowerChat,
    data: useObserver(() => {
      return {
        init: store.init,
        loading: store.loading,
        comments: store.comments.reverse(),
        powerChat: store.powerChat,
        report: store.report,
      };
    }),
    isAll: useObserver(() => store.isAll),
  };
};

export default MatchCheerStore;
